import React from 'react';
import MDReactComponent from 'markdown-react-js';
import { Link } from 'react-router';
import _ from 'lodash';
import Picture from './Picture.jsx';
import TweetRenderer from './TweetRenderer.jsx';
import InstagramRenderer from './InstagramRenderer.jsx';
import QuizRenderer from './QuizRenderer.jsx';
import VideoPlayerPlaceholder from './VideoPlayerPlaceholder.jsx';

const externalLink = (link, child) => <a href={link} target="blank">{child}</a>;

export default class MarkdownConverter extends React.PureComponent {
  constructor() {
    super();
    this.key = 0;
    this.handleIterate = this.handleIterate.bind(this);
  }

  handleIterate(Tag, props, children) {
    switch (Tag) {
    case 'a':
      if (props.href.startsWith('http')) {
        return <a key={this.key++} {...props} target="_blank">{children}</a>;
      } else if (props.href.startsWith('mailto:')) {
        return <a key={this.key++} {...props}>{children}</a>;
      }

      return <Link to={props.href} key={props.href}>{children}</Link>;
    case 'img':
      return <Picture key={this.key++} {...props} />;
    case 'p':
      // p-tags cannot contain non text elements, which causes the page to break
      // when inserting inline videos. Divs can obviously contain other divs.
      return <div key={this.key++} className="paragraph" {...props}>{children}</div>;
    case 'code':
      return this.renderWidget(children, props.key);
    case 'pre':
      return <div key={this.key++} className="shortcode-block">{children}</div>;
    // React complains if we don't return self-closing tags as, well, sel-closing tags
    // ref: [http://xahlee.info/js/html5_non-closing_tag.html]
    // NOTE: yes, "img" is duplicate here, just for clarity (if we remove/rework <Picture>
    // above we still have the correct behavior)
    case 'area':
    case 'base':
    case 'br':
    case 'col':
    case 'command':
    case 'embed':
    case 'hr':
    case 'img': // eslint-disable-line no-duplicate-case
    case 'input':
    case 'keygen':
    case 'link':
    case 'meta':
    case 'param':
    case 'source':
    case 'track':
    case 'wbr':
      return <Tag key={this.key++} {...props} />;
    default:
      break;
    }

    return <Tag key={this.key++} {...props}>{children}</Tag>;
  }

  renderWidget(children, key) {
    try {
      const content = children[0].trim();
      let widget;
      if (/^<.*>$/.test(content)) {
        widget = {
          type: 'html',
          content,
        };
      } else {
        widget = JSON.parse(children[0]);
      }

      switch (widget.type) {
      case 'iframe':
      case 'video-embed':
      case 'acast':
        return (
          <div key={this.key++} className={widget.type}>
            <div>
              <iframe src={widget.data.url} />
            </div>
          </div>
        );
      case 'tweet':
        return <TweetRenderer key={this.key++} id={widget.data.id} />;
      case 'instagram':
        return <InstagramRenderer key={this.key++} id={widget.data.id} />;
      case 'facebook':
        return <iframe key={this.key++} className="facebook-embed" src={widget.data.url} width={widget.data.width} height={widget.data.height} />;
      case 'youtube':
        return (
          <div key={this.key++} className="video-embed youtube">
            <div>
              <iframe src={`https://www.youtube.com/embed/${widget.data.id}`} allowFullScreen />
            </div>
          </div>
        );
      case 'mpxImage':
        const imageText = _.get(widget, 'data.pictureText');
        const imageLink = _.get(widget, 'data.pictureUrl');
        const img = (<div>
            <Picture src={ widget.data.guid } alt={ this.props.article.title } />
            {imageText && <div className="media-description">{imageText}</div>}
          </div>);
        const pic = imageLink ? externalLink(imageLink, img) : img;

        return (<div key={this.key++}>{pic}</div>);
      case 'mpxVideo':
        const article = this.props.article;
        const video = { mediaGuid: widget.data.guid, posterUrl: widget.data.poster };
        const videoText = _.get(widget, 'data.videoText');

        return (
          <div key={this.key++} className="thumbnail thumbnail-video">
            <VideoPlayerPlaceholder video={video} article={article} csid={this.props.csid} videoElementId={`inline-video-${widget.data.guid}-${key}`} />
            { videoText && <div className="media-description">{videoText}</div> }
          </div>
        );
      case 'quiz':
        return <QuizRenderer key={this.key++} data={widget.data} />;
      case 'html':
        return <div key={this.key++} dangerouslySetInnerHTML={{__html: widget.content}} />;
      default:
        console.log(`Unknown shortcode type "${widget.type}" for widget`, widget);
      }
    } catch (err) {
      console.log('Failed to parse widget', children[0], err);
    }

    return false;
  }

  render() {
    if (!this.props.markdown) return null;
    this.key = 0;
    return (
      <div ref="container" className="markdown-converter">
        <MDReactComponent
          text={this.props.markdown}
          onIterate={this.handleIterate}
          presetName="commonmark"
        />
      </div>
    );
  }
}

MarkdownConverter.propTypes = {
  markdown: React.PropTypes.string.isRequired,
  article: React.PropTypes.object,
  csid: React.PropTypes.string,
  sovSeed: React.PropTypes.string,
};



// WEBPACK FOOTER //
// ./src/component/MarkdownConverter.jsx